/**
 * GIFT CART — v2
 * Ajusta somente a apresentação dos produtos com a tag "gift-hidden".
 *
 * Não altera preço, desconto, item do carrinho ou dados enviados ao ERP.
 * Funciona no cart drawer e na página completa do carrinho.
 */

(() => {
  'use strict';

  const GIFT_TAG = 'gift-hidden';
  const GIFT_LABEL = 'BRINDE';
  const ROOT = window.Shopify?.routes?.root || '/';

  const productTagCache = new Map();
  let updateTimer = null;
  let isRunning = false;
  let rerunRequested = false;

  function injectStyles() {
    if (document.getElementById('gift-cart-styles')) return;

    const style = document.createElement('style');
    style.id = 'gift-cart-styles';
    style.textContent = `
      .cart-item[data-gift-cart="true"] .cart-item__details > .product-option:not(.gift-cart__label),
      .cart-item[data-gift-cart="true"] .cart-item__details > .cart-item__discounted-prices,
      .cart-item[data-gift-cart="true"] .cart-item__details > dl,
      .cart-item[data-gift-cart="true"] .cart-item__details > p.product-option,
      .cart-item[data-gift-cart="true"] .cart-item__details > .discounts,
      .cart-item[data-gift-cart="true"] .cart-drawer__installment,
      .cart-item[data-gift-cart="true"] .cart-item__quantity {
        display: none !important;
      }

      .cart-item[data-gift-cart="true"] .gift-cart__label {
        display: block !important;
        margin-top: 6px;
        font-size: 1.2rem;
        font-weight: 600;
        line-height: 1.3;
        letter-spacing: 0.08rem;
        text-transform: uppercase;
      }

      .cart-item[data-gift-cart="true"] .cart-item__price-wrapper {
        font-weight: 600;
        text-transform: uppercase;
      }

      .cart-item[data-gift-cart="true"] .gift-cart__name {
        color: inherit;
        text-decoration: none;
        cursor: default;
        pointer-events: none;
      }

      .cart-item[data-gift-cart="true"] .cart-item__media .cart-item__link {
        display: none !important;
        pointer-events: none !important;
      }
    `;

    document.head.appendChild(style);
  }

  function normalizeHandle(item) {
    if (item?.handle) return item.handle;

    const url = String(item?.url || '');
    const match = url.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  async function productHasGiftTag(item) {
    const handle = normalizeHandle(item);
    if (!handle) return false;

    if (productTagCache.has(handle)) {
      return productTagCache.get(handle);
    }

    const request = fetch(`${ROOT}products/${encodeURIComponent(handle)}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Produto ${handle}: HTTP ${response.status}`);
        }
        return response.json();
      })
      .then((product) => {
        const tags = Array.isArray(product.tags)
          ? product.tags
          : String(product.tags || '').split(',');

        return tags.some(
          (tag) => String(tag).trim().toLowerCase() === GIFT_TAG.toLowerCase()
        );
      })
      .catch((error) => {
        console.warn('[Gift Cart] Não foi possível consultar as tags:', error);
        return false;
      });

    productTagCache.set(handle, request);
    return request;
  }

  function removeProductLinks(row) {
    row.querySelectorAll('.cart-item__media .cart-item__link').forEach((link) => {
      link.remove();
    });

    const nameLink = row.querySelector('a.cart-item__name');

    if (nameLink) {
      const name = document.createElement('span');
      name.className = `${nameLink.className} gift-cart__name`;
      name.textContent = nameLink.textContent.trim();
      nameLink.replaceWith(name);
    } else {
      row.querySelector('.cart-item__name')?.classList.add('gift-cart__name');
    }
  }

  function addGiftLabelBesideName(row) {
    const details = row.querySelector('.cart-item__details');
    const name = details?.querySelector('.cart-item__name');

    if (!details || !name || details.querySelector('.gift-cart__label')) return;

    const label = document.createElement('div');
    label.className = 'gift-cart__label';
    label.textContent = GIFT_LABEL;

    name.insertAdjacentElement('afterend', label);
  }

  function replaceTotalPrices(row) {
    row.querySelectorAll('.cart-item__totals .cart-item__price-wrapper').forEach((wrapper) => {
      wrapper.replaceChildren();

      const label = document.createElement('span');
      label.className = 'price price--end gift-cart__label';
      label.textContent = GIFT_LABEL;

      wrapper.appendChild(label);
    });
  }

  function applyGiftRow(row) {
    if (!row) return;

    row.dataset.giftCart = 'true';
    removeProductLinks(row);
    addGiftLabelBesideName(row);
    replaceTotalPrices(row);
  }

  async function updateGiftPresentation() {
    if (isRunning) {
      rerunRequested = true;
      return;
    }

    isRunning = true;
    rerunRequested = false;

    try {
      const response = await fetch(`${ROOT}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error(`Carrinho: HTTP ${response.status}`);
      }

      const cart = await response.json();

      await Promise.all(
        cart.items.map(async (item, index) => {
          const isGift = await productHasGiftTag(item);
          if (!isGift) return;

          const position = index + 1;
          applyGiftRow(document.getElementById(`CartDrawer-Item-${position}`));
          applyGiftRow(document.getElementById(`CartItem-${position}`));
        })
      );
    } catch (error) {
      console.warn('[Gift Cart] Não foi possível atualizar a apresentação:', error);
    } finally {
      isRunning = false;

      if (rerunRequested) {
        scheduleUpdate(80);
      }
    }
  }

  function scheduleUpdate(delay = 140) {
    window.clearTimeout(updateTimer);
    updateTimer = window.setTimeout(updateGiftPresentation, delay);
  }

  function start() {
    injectStyles();
    scheduleUpdate(0);

    const observer = new MutationObserver((mutations) => {
      const cartChanged = mutations.some((mutation) => {
        const target = mutation.target;

        return (
          target instanceof Element &&
          (
            target.closest('cart-drawer') ||
            target.closest('cart-items') ||
            target.id === 'CartDrawer-CartItems' ||
            target.id === 'main-cart-items'
          )
        );
      });

      if (cartChanged) scheduleUpdate();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    document.addEventListener('shopify:section:load', () => scheduleUpdate(0));
    document.addEventListener('cart:updated', () => scheduleUpdate(0));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
